import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, Check, Copy, PartyPopper } from "lucide-react"
import { Button, Modal, cx } from "../ui"
import { copyText } from "../../lib/format"

export const PROMO_CODE = "LAUNCH30"
export const PROMO_PERCENT = 30

const SEEN_KEY = "loomina.promo.launch30"

/**
 * The launch offer, shown once.
 *
 * Once per browser, and only to signed-out visitors - somebody who already has
 * an account is not a new customer, and the landing page sends them straight
 * to their projects anyway. It waits for the hero animation to finish before
 * appearing, so the first thing a visitor sees is the product rather than a
 * box asking them to buy it.
 */
export function LaunchPromoDialog() {
	const [open, setOpen] = useState(false)
	const [copied, setCopied] = useState(false)

	useEffect(() => {
		let seen = false
		try {
			seen = window.localStorage.getItem(SEEN_KEY) === "1"
		} catch {
			// Private browsing blocks storage. Showing it every visit is a
			// better failure than never showing it at all.
			seen = false
		}
		if (seen) return undefined

		const timer = window.setTimeout(() => setOpen(true), 1600)
		return () => window.clearTimeout(timer)
	}, [])

	function dismiss() {
		setOpen(false)
		try {
			window.localStorage.setItem(SEEN_KEY, "1")
		} catch {
			/* nothing to remember it with; it will show again next visit */
		}
	}

	async function copy() {
		const ok = await copyText(PROMO_CODE)
		if (!ok) return
		setCopied(true)
		window.setTimeout(() => setCopied(false), 2400)
	}

	return (
		<Modal open={open} onClose={dismiss} title="Launch promotion" size="sm">
			<div className="text-center">
				<span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-brand-50 text-brand-700">
					<PartyPopper size={22} aria-hidden />
				</span>

				<p className="mt-4 text-3xl font-semibold tracking-tight text-ink-900">
					{PROMO_PERCENT}% off your first plan
				</p>
				<p className="mx-auto mt-2 max-w-xs text-[0.875rem] leading-relaxed text-ink-600">
					We have just launched. New customers get {PROMO_PERCENT}% off Plus or Max —
					enter the code at checkout.
				</p>

				<button
					type="button"
					onClick={copy}
					aria-label={`Copy the promotion code ${PROMO_CODE}`}
					className={cx(
						"mt-5 inline-flex items-center gap-3 rounded-xl border-2 border-dashed px-4 py-3",
						"transition-colors",
						copied
							? "border-state-closed bg-state-closed-soft"
							: "border-brand-300 bg-brand-50 hover:border-brand-400"
					)}
				>
					<span className="font-mono text-xl font-bold tracking-[0.15em] text-brand-900">
						{PROMO_CODE}
					</span>
					<span
						className={cx(
							"flex items-center gap-1.5 text-2xs font-semibold",
							copied ? "text-state-closed-ink" : "text-brand-700"
						)}
					>
						{copied ? (
							<>
								<Check size={13} aria-hidden />
								Copied
							</>
						) : (
							<>
								<Copy size={13} aria-hidden />
								Copy
							</>
						)}
					</span>
				</button>

				<div className="mt-6 flex flex-col gap-2">
					<Button
						as={Link}
						to="/register"
						variant="primary"
						size="lg"
						iconRight={ArrowRight}
						onClick={dismiss}
						className="w-full"
					>
						Create your account
					</Button>
					<button
						type="button"
						onClick={dismiss}
						className="py-1 text-[0.8125rem] font-medium text-ink-500 transition-colors hover:text-ink-900"
					>
						Maybe later
					</button>
				</div>

				<p className="mt-4 text-2xs leading-relaxed text-ink-400">
					New customers only. The free plan needs no code and no card.
				</p>
			</div>
		</Modal>
	)
}

/**
 * The same offer, left on the pricing table.
 *
 * Somebody who dismissed the dialog and then scrolled down to compare plans is
 * exactly the person the code is for, and by then it is gone.
 */
export function LaunchPromoBanner({ className }) {
	const [copied, setCopied] = useState(false)

	async function copy() {
		const ok = await copyText(PROMO_CODE)
		if (!ok) return
		setCopied(true)
		window.setTimeout(() => setCopied(false), 2400)
	}

	return (
		<div
			className={cx(
				"inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-full",
				"border border-brand-200 bg-brand-50 px-4 py-2",
				className
			)}
		>
			<span className="rounded-full bg-brand-700 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider text-white">
				Launch promotion
			</span>
			<span className="text-[0.875rem] text-brand-900">
				{PROMO_PERCENT}% off for new customers with
			</span>
			<button
				type="button"
				onClick={copy}
				aria-label={`Copy the promotion code ${PROMO_CODE}`}
				className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 font-mono text-[0.8125rem] font-bold tracking-wider text-brand-900 ring-1 ring-inset ring-brand-300 transition-colors hover:ring-brand-400"
			>
				{PROMO_CODE}
				{copied ? (
					<Check size={12} className="text-state-closed" aria-hidden />
				) : (
					<Copy size={12} className="text-brand-600" aria-hidden />
				)}
			</button>
		</div>
	)
}
